import React, { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../config/api';
import { useAuth } from '../context/AuthContext';
import {
  FiPlus,
  FiCheck,
  FiX,
  FiEdit2,
  FiFilter,
  FiCalendar,
  FiClock,
  FiUser,
  FiBriefcase,
  FiFileText,
  FiSave,
  FiList,
  FiGrid,
  FiColumns,
} from 'react-icons/fi';
import './Timesheets.css';
import LoadingButton from '../components/LoadingButton';
import IconButton from '../components/IconButton';
import ListSearchBar from '../components/ListSearchBar';
import MoreMenu from '../components/MoreMenu';
import Modal from '../components/Modal';
import ListPager from '../components/ListPager';
import { queryKeys, staleTimes } from '../config/queryKeys';
import {
  DEFAULT_LIST_LIMIT,
  normalizePagedList,
  paginationFromHeaders,
} from '../utils/listPagination';
import { appAlert } from '../utils/appDialog';
import { usePermissions } from '../context/PermissionsContext';
import { ProjectSelector } from '../components/ProjectSelector';

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
];

const VIEW_OPTIONS = [
  { id: 'list', label: 'List', icon: FiList },
  { id: 'tile', label: 'Tiles', icon: FiGrid },
  { id: 'swimlane', label: 'Swim lane', icon: FiColumns },
];

const SWIM_LANES = [
  { status: 'draft', label: 'Draft' },
  { status: 'submitted', label: 'Submitted' },
  { status: 'approved', label: 'Approved' },
  { status: 'rejected', label: 'Rejected' },
];

const VIEW_STORAGE_KEY = 'godash.timesheets.viewMode';

function formatConsultant(ts) {
  const name = [ts.user_first_name, ts.user_last_name].filter(Boolean).join(' ').trim();
  if (name) return name;
  return ts.user_email || '—';
}

function formatStatus(status) {
  return (status || 'draft').replace(/^\w/, (c) => c.toUpperCase());
}

function parseDateParts(iso) {
  if (!iso) return { dow: '', day: '', mon: '', full: '' };
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { dow: '', day: '', mon: '', full: String(iso).slice(0, 10) };
  return {
    dow: d.toLocaleDateString(undefined, { weekday: 'short' }),
    day: String(d.getDate()),
    mon: d.toLocaleDateString(undefined, { month: 'short', year: 'numeric' }),
    full: d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }),
  };
}

function candidateLabel(ts) {
  if (ts.candidate_first_name || ts.candidate_last_name) {
    return `${ts.candidate_first_name || ''} ${ts.candidate_last_name || ''}`.trim();
  }
  return null;
}

function jobLabel(ts) {
  return ts.job_title ? `${ts.job_title}${ts.job_company ? ` · ${ts.job_company}` : ''}` : null;
}

function readStoredViewMode() {
  try {
    const stored = localStorage.getItem(VIEW_STORAGE_KEY);
    if (VIEW_OPTIONS.some((v) => v.id === stored)) return stored;
  } catch {
    /* ignore */
  }
  return 'tile';
}

function TimesheetActions({ ts, user, openEdit, submitMutation, approveMutation }) {
  const submitting = submitMutation.isLoading && submitMutation.variables === ts.id;
  const approving =
    approveMutation.isLoading &&
    approveMutation.variables?.id === ts.id &&
    approveMutation.variables?.action === 'approve';
  const rejecting =
    approveMutation.isLoading &&
    approveMutation.variables?.id === ts.id &&
    approveMutation.variables?.action === 'reject';
  const approveBusy = approveMutation.isLoading && approveMutation.variables?.id === ts.id;
  const canEditSubmit =
    ts.status === 'draft' && (user?.role === 'consultant' || user?.role === 'admin');
  const canApproveReject = user?.role === 'admin' && ts.status === 'submitted';

  return (
    <div className="timesheet-card__actions">
      <MoreMenu
        label={`Actions for timesheet ${ts.id}`}
        size="sm"
        items={[
          {
            id: 'edit',
            label: 'Edit',
            icon: FiEdit2,
            hidden: !canEditSubmit,
            onSelect: () => openEdit(ts),
          },
          {
            id: 'submit',
            label: submitting ? 'Submitting…' : 'Submit',
            icon: FiCheck,
            hidden: !canEditSubmit,
            disabled: submitting,
            onSelect: () => submitMutation.mutate(ts.id),
          },
          {
            id: 'approve',
            label: approving ? 'Approving…' : 'Approve',
            icon: FiCheck,
            hidden: !canApproveReject,
            disabled: approveBusy,
            onSelect: () => approveMutation.mutate({ id: ts.id, action: 'approve' }),
          },
          {
            id: 'reject',
            label: rejecting ? 'Rejecting…' : 'Reject',
            icon: FiX,
            danger: true,
            hidden: !canApproveReject,
            disabled: approveBusy,
            onSelect: () => approveMutation.mutate({ id: ts.id, action: 'reject' }),
          },
        ]}
      />
    </div>
  );
}

function TimesheetCard({ ts, user, openEdit, submitMutation, approveMutation, compact = false }) {
  const { dow, day, mon } = parseDateParts(ts.date);
  const consultantName = formatConsultant(ts);
  const candidate = candidateLabel(ts);
  const job = jobLabel(ts);

  return (
    <article className={`timesheet-card${compact ? ' timesheet-card--compact' : ''}`}>
      <div className="timesheet-card__date-block">
        <span className="timesheet-card__dow">{dow}</span>
        <span className="timesheet-card__day">{day}</span>
        <span className="timesheet-card__mon">{mon}</span>
      </div>
      <div className="timesheet-card__main">
        <div className="timesheet-card__top">
          <span className="timesheet-card__hours-pill">
            <FiClock aria-hidden />
            {ts.hours}h
          </span>
          <span className={`timesheet-card__status timesheet-card__status--${ts.status || 'draft'}`}>
            {formatStatus(ts.status)}
          </span>
        </div>
        <p className="timesheet-card__description">
          {ts.description != null ? (
            ts.description || <em className="timesheet-card__muted">No description</em>
          ) : ts.has_description ? (
            <em className="timesheet-card__muted">Description on detail</em>
          ) : (
            <em className="timesheet-card__muted">No description</em>
          )}
        </p>
        <div className="timesheet-card__chips">
          {user?.role === 'admin' && (
            <span className="timesheet-chip timesheet-chip--user">
              <FiUser aria-hidden />
              <span className="timesheet-chip__text">
                <strong>{consultantName}</strong>
                {ts.user_email && <span className="timesheet-chip__sub">{ts.user_email}</span>}
              </span>
            </span>
          )}
          {candidate && (
            <span className="timesheet-chip">
              <FiUser aria-hidden />
              {candidate}
            </span>
          )}
          {job && (
            <span className="timesheet-chip">
              <FiBriefcase aria-hidden />
              {job}
            </span>
          )}
          {ts.project_name && (
            <span className="timesheet-chip">
              <FiBriefcase aria-hidden />
              {ts.project_code ? `${ts.project_code} · ` : ''}{ts.project_name}
              {ts.billable ? ' · Billable' : ''}
            </span>
          )}
        </div>
      </div>
      <TimesheetActions
        ts={ts}
        user={user}
        openEdit={openEdit}
        submitMutation={submitMutation}
        approveMutation={approveMutation}
      />
    </article>
  );
}

const Timesheets = () => {
  const { user } = useAuth();
  const { hasAnyPermission } = usePermissions();
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [editingTimesheet, setEditingTimesheet] = useState(null);
  const [showFilters, setShowFilters] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [page, setPage] = useState(1);
  const [viewMode, setViewMode] = useState(readStoredViewMode);
  const [formData, setFormData] = useState({
    candidate_id: '',
    job_id: '',
    project_id: '',
    billable: true,
    date: new Date().toISOString().split('T')[0],
    hours: '',
    description: '',
  });

  const canCreate = user?.role === 'consultant' || user?.role === 'admin';
  const canUseProjects =
    user?.role === 'admin' ||
    hasAnyPermission(['erp_projects_view', 'erp_projects_manage', 'tab_projects']);

  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [filterStatus, searchDebounced, viewMode]);

  const listFilters = {
    page,
    limit: DEFAULT_LIST_LIMIT,
    status: viewMode === 'swimlane' ? '' : filterStatus,
    search: searchDebounced,
  };

  const { data: timesheetsPage, isLoading, isFetching } = useQuery(
    queryKeys.timesheets(listFilters),
    () =>
      api
        .get('/timesheets', {
          params: {
            limit: DEFAULT_LIST_LIMIT,
            page,
            status: viewMode === 'swimlane' ? undefined : filterStatus || undefined,
            q: searchDebounced || undefined,
          },
        })
        .then((res) => paginationFromHeaders(res, Array.isArray(res.data) ? res.data : [])),
    { keepPreviousData: true, staleTime: staleTimes.lists }
  );

  const timesheets = timesheetsPage?.items || [];
  const pagination = timesheetsPage?.pagination;

  const { data: candidates } = useQuery(
    queryKeys.timesheetCandidates(user?.role),
    () =>
      user?.role === 'admin'
        ? api.get('/candidates').then((res) => res.data)
        : api.get('/candidates/assigned').then((res) => res.data),
    { enabled: !!user && (user?.role === 'consultant' || user?.role === 'admin') }
  );

  const { data: jobs } = useQuery(
    queryKeys.jobsPicker(200),
    () =>
      api
        .get('/jobs', { params: { limit: 200, page: 1 } })
        .then((res) => normalizePagedList(res.data, { itemsKey: 'jobs' }).items),
    { enabled: !!user && (user?.role === 'consultant' || user?.role === 'admin') }
  );

  const filteredTimesheets = timesheets;

  const swimlaneGroups = useMemo(() => {
    const groups = Object.fromEntries(SWIM_LANES.map((lane) => [lane.status, []]));
    for (const ts of filteredTimesheets) {
      const key = SWIM_LANES.some((l) => l.status === ts.status) ? ts.status : 'draft';
      groups[key].push(ts);
    }
    return groups;
  }, [filteredTimesheets]);

  const stats = useMemo(() => {
    const list = filteredTimesheets;
    const hours = list.reduce((s, t) => s + (parseFloat(t.hours) || 0), 0);
    const pending = list.filter((t) => t.status === 'submitted').length;
    const drafts = list.filter((t) => t.status === 'draft').length;
    return { count: pagination?.total ?? list.length, hours, pending, drafts };
  }, [filteredTimesheets, pagination?.total]);

  const invalidateTimesheets = () => queryClient.invalidateQueries(queryKeys.timesheetsRoot);

  const createMutation = useMutation((data) => api.post('/timesheets', data), {
    onSuccess: () => {
      invalidateTimesheets();
      closeModal();
    },
    onError: (e) => void appAlert(e.response?.data?.error || e.message),
  });

  const updateMutation = useMutation(({ id, data }) => api.put(`/timesheets/${id}`, data), {
    onSuccess: () => {
      invalidateTimesheets();
      closeModal();
    },
    onError: (e) => void appAlert(e.response?.data?.error || e.message),
  });

  const submitMutation = useMutation((id) => api.post(`/timesheets/${id}/submit`), {
    onSuccess: () => invalidateTimesheets(),
    onError: (e) => void appAlert(e.response?.data?.error || e.message),
  });

  const approveMutation = useMutation(
    ({ id, action }) => api.post(`/timesheets/${id}/approve`, { action }),
    {
      onSuccess: () => invalidateTimesheets(),
      onError: (e) => void appAlert(e.response?.data?.error || e.message),
    }
  );

  const closeModal = () => {
    setShowModal(false);
    setEditingTimesheet(null);
    setFormData({
      candidate_id: '',
      job_id: '',
      project_id: '',
      billable: true,
      date: new Date().toISOString().split('T')[0],
      hours: '',
      description: '',
    });
  };

  const openCreate = () => {
    setEditingTimesheet(null);
    setFormData({
      candidate_id: '',
      job_id: '',
      project_id: '',
      billable: true,
      date: new Date().toISOString().split('T')[0],
      hours: '',
      description: '',
    });
    setShowModal(true);
  };

  const openEdit = async (ts) => {
    try {
      const detail = await api.get(`/timesheets/${ts.id}`).then((res) => res.data);
      const row = detail || ts;
      const d = row.date ? String(row.date).slice(0, 10) : new Date().toISOString().split('T')[0];
      setEditingTimesheet(row);
      setFormData({
        candidate_id: row.candidate_id != null ? String(row.candidate_id) : '',
        job_id: row.job_id != null ? String(row.job_id) : '',
        project_id: row.project_id || '',
        billable: Boolean(row.billable),
        date: d,
        hours: row.hours != null ? String(row.hours) : '',
        description: row.description || '',
      });
      setShowModal(true);
    } catch (e) {
      void appAlert(e.response?.data?.error || e.message);
    }
  };

  const handleViewChange = (next) => {
    setViewMode(next);
    try {
      localStorage.setItem(VIEW_STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = {
      candidate_id: formData.candidate_id ? parseInt(formData.candidate_id, 10) : null,
      job_id: formData.job_id ? parseInt(formData.job_id, 10) : null,
      project_id: formData.project_id || null,
      billable: formData.project_id ? Boolean(formData.billable) : false,
      date: formData.date,
      hours: formData.hours,
      description: formData.description || null,
    };
    if (editingTimesheet) {
      updateMutation.mutate({ id: editingTimesheet.id, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const hasActiveFilters = Boolean(
    (viewMode !== 'swimlane' && filterStatus) || search.trim()
  );

  const cardProps = {
    user,
    openEdit,
    submitMutation,
    approveMutation,
  };

  if (isLoading) {
    return <div className="loading">Loading timesheets...</div>;
  }

  return (
    <div className="timesheets-page list-page">
      <div className="page-header">
        <h1>Timesheets</h1>
        <div className="list-page-header-actions page-toolbar">
          <IconButton
            icon={FiFilter}
            label={showFilters ? 'Hide filters' : 'Show filters'}
            active={showFilters}
            onClick={() => setShowFilters(!showFilters)}
          />
          <div className="timesheets-view-toggle" role="group" aria-label="View mode">
            {VIEW_OPTIONS.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                type="button"
                className={viewMode === id ? 'active' : ''}
                onClick={() => handleViewChange(id)}
                title={label}
                aria-label={label}
                aria-pressed={viewMode === id}
              >
                <Icon />
                <span className="timesheets-view-toggle__label">{label}</span>
              </button>
            ))}
          </div>
          {canCreate && (
            <IconButton icon={FiPlus} label="Log time" variant="primary" onClick={openCreate} />
          )}
        </div>
      </div>

      <div className="timesheets-stats">
        <div className="timesheets-stat-card">
          <FiFileText className="timesheets-stat-icon" aria-hidden />
          <div>
            <span className="timesheets-stat-value">{stats.count}</span>
            <span className="timesheets-stat-label">Entries (filtered)</span>
          </div>
        </div>
        <div className="timesheets-stat-card timesheets-stat-card--hours">
          <FiClock className="timesheets-stat-icon" aria-hidden />
          <div>
            <span className="timesheets-stat-value">{stats.hours.toFixed(1)}</span>
            <span className="timesheets-stat-label">Hours total</span>
          </div>
        </div>
        <div className="timesheets-stat-card timesheets-stat-card--pending">
          <FiCalendar className="timesheets-stat-icon" aria-hidden />
          <div>
            <span className="timesheets-stat-value">{stats.pending}</span>
            <span className="timesheets-stat-label">Awaiting approval</span>
          </div>
        </div>
        <div className="timesheets-stat-card">
          <FiEdit2 className="timesheets-stat-icon" aria-hidden />
          <div>
            <span className="timesheets-stat-value">{stats.drafts}</span>
            <span className="timesheets-stat-label">Drafts</span>
          </div>
        </div>
      </div>

      {showFilters && (
        <div className="list-filters-panel list-toolbar-panel timesheets-filters">
          <div className="filter-row list-toolbar-row">
            <div className="filter-group filter-group--wide list-toolbar-search">
              <ListSearchBar
                id="ts-filter-search"
                value={search}
                onChange={setSearch}
                placeholder="Search description, candidate, job…"
              />
            </div>
            {viewMode !== 'swimlane' && (
              <div className="filter-group">
                <label htmlFor="ts-filter-status">Status</label>
                <select
                  id="ts-filter-status"
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  {STATUS_OPTIONS.map((o) => (
                    <option key={o.value || 'all'} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {hasActiveFilters && (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setFilterStatus('');
                  setSearch('');
                }}
              >
                <FiX /> Clear
              </button>
            )}
          </div>
        </div>
      )}

      {filteredTimesheets.length === 0 ? (
        <div className="timesheets-empty">
          <FiCalendar className="timesheets-empty-icon" aria-hidden />
          {timesheets?.length > 0 ? (
            <>
              <p>No timesheets match your filters.</p>
              {hasActiveFilters && (
                <IconButton
                  icon={FiX}
                  label="Clear filters"
                  onClick={() => {
                    setFilterStatus('');
                    setSearch('');
                  }}
                />
              )}
            </>
          ) : (
            <>
              <p>No timesheets yet.</p>
              {canCreate && (
                <button type="button" className="btn btn-primary" onClick={openCreate}>
                  <FiPlus /> Log your first entry
                </button>
              )}
            </>
          )}
        </div>
      ) : viewMode === 'list' ? (
        <div className="timesheets-list-wrap glass-surface">
          <table className="data-table timesheets-list-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Hours</th>
                <th>Status</th>
                {user?.role === 'admin' && <th>Consultant</th>}
                <th>Candidate</th>
                <th>Job</th>
                <th>Project</th>
                <th>Description</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredTimesheets.map((ts) => {
                const { full } = parseDateParts(ts.date);
                return (
                  <tr key={ts.id}>
                    <td>{full || '—'}</td>
                    <td>{ts.hours}h</td>
                    <td>
                      <span className={`timesheet-card__status timesheet-card__status--${ts.status || 'draft'}`}>
                        {formatStatus(ts.status)}
                      </span>
                    </td>
                    {user?.role === 'admin' && <td>{formatConsultant(ts)}</td>}
                    <td>{candidateLabel(ts) || '—'}</td>
                    <td>{jobLabel(ts) || '—'}</td>
                    <td>
                      {ts.project_name
                        ? `${ts.project_code ? `${ts.project_code} · ` : ''}${ts.project_name}${ts.billable ? ' · Billable' : ''}`
                        : 'Unlinked'}
                    </td>
                    <td className="timesheets-list-desc">
                      {ts.description != null
                        ? ts.description || '—'
                        : ts.has_description
                          ? 'Has description'
                          : '—'}
                    </td>
                    <td className="timesheets-list-actions">
                      <TimesheetActions
                        ts={ts}
                        user={user}
                        openEdit={openEdit}
                        submitMutation={submitMutation}
                        approveMutation={approveMutation}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : viewMode === 'swimlane' ? (
        <div className="timesheets-swimlanes">
          {SWIM_LANES.map((lane) => {
            const items = swimlaneGroups[lane.status] || [];
            return (
              <section
                key={lane.status}
                className={`timesheets-swimlane timesheets-swimlane--${lane.status}`}
              >
                <header className="timesheets-swimlane__head">
                  <h2>
                    <span className={`timesheet-card__status timesheet-card__status--${lane.status}`}>
                      {lane.label}
                    </span>
                  </h2>
                  <span className="timesheets-swimlane__count">{items.length}</span>
                </header>
                <div className="timesheets-swimlane__body">
                  {items.length === 0 ? (
                    <p className="timesheets-swimlane__empty">No entries</p>
                  ) : (
                    items.map((ts) => (
                      <TimesheetCard key={ts.id} ts={ts} compact {...cardProps} />
                    ))
                  )}
                </div>
              </section>
            );
          })}
        </div>
      ) : (
        <div className="timesheets-card-grid">
          {filteredTimesheets.map((ts) => (
            <TimesheetCard key={ts.id} ts={ts} {...cardProps} />
          ))}
        </div>
      )}

      <Modal
        open={showModal}
        onClose={closeModal}
        contentClassName="timesheet-modal"
        ariaLabel={editingTimesheet ? 'Edit timesheet' : 'Log time'}
      >
        <h2>{editingTimesheet ? 'Edit timesheet' : 'Log time'}</h2>
        <p className="timesheet-modal__hint">
          {editingTimesheet
            ? 'Only draft entries can be edited. Submit when ready for approval.'
            : 'Add hours against a candidate and/or job. Submit when you are ready.'}
        </p>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="ts-date">Date</label>
            <input
              id="ts-date"
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              required
              disabled={editingTimesheet && editingTimesheet.status !== 'draft'}
            />
          </div>
          {canUseProjects && (
            <>
              <div className="form-group">
                <label htmlFor="ts-project">Delivery project (optional)</label>
                <ProjectSelector
                  id="ts-project"
                  value={formData.project_id}
                  onChange={(projectId) =>
                    setFormData({
                      ...formData,
                      project_id: projectId,
                      billable: projectId ? formData.billable : false,
                    })
                  }
                  disabled={editingTimesheet && editingTimesheet.status !== 'draft'}
                />
              </div>
              {formData.project_id && (
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={Boolean(formData.billable)}
                      onChange={(event) =>
                        setFormData({ ...formData, billable: event.target.checked })
                      }
                      disabled={editingTimesheet && editingTimesheet.status !== 'draft'}
                    />
                    Billable project time
                  </label>
                </div>
              )}
            </>
          )}
          <div className="form-group">
            <label htmlFor="ts-hours">Hours</label>
            <input
              id="ts-hours"
              type="number"
              step="0.5"
              min="0"
              value={formData.hours}
              onChange={(e) => setFormData({ ...formData, hours: e.target.value })}
              required
              disabled={editingTimesheet && editingTimesheet.status !== 'draft'}
            />
          </div>
          <div className="form-group">
            <label htmlFor="ts-candidate">Candidate (optional)</label>
            <select
              id="ts-candidate"
              value={formData.candidate_id}
              onChange={(e) => setFormData({ ...formData, candidate_id: e.target.value })}
              disabled={editingTimesheet && editingTimesheet.status !== 'draft'}
            >
              <option value="">— Select candidate —</option>
              {candidates?.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.first_name} {c.last_name}
                  {c.email ? ` (${c.email})` : ''}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="ts-job">Job (optional)</label>
            <select
              id="ts-job"
              value={formData.job_id}
              onChange={(e) => setFormData({ ...formData, job_id: e.target.value })}
              disabled={editingTimesheet && editingTimesheet.status !== 'draft'}
            >
              <option value="">— Select job —</option>
              {jobs?.map((job) => (
                <option key={job.id} value={job.id}>
                  {job.title} — {job.company}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="ts-desc">Description</label>
            <textarea
              id="ts-desc"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              required
              rows={4}
              disabled={editingTimesheet && editingTimesheet.status !== 'draft'}
            />
          </div>
          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={closeModal}>
              Cancel
            </button>
            {(!editingTimesheet || editingTimesheet.status === 'draft') && (
              <LoadingButton
                type="submit"
                className="btn btn-primary"
                icon={FiSave}
                loading={createMutation.isLoading || updateMutation.isLoading}
                loadingLabel="Saving…"
              >
                {editingTimesheet ? 'Save changes' : 'Create'}
              </LoadingButton>
            )}
          </div>
        </form>
      </Modal>

      <ListPager
        page={pagination?.page || page}
        pageCount={pagination?.pageCount || 1}
        total={pagination?.total}
        limit={pagination?.limit || DEFAULT_LIST_LIMIT}
        disabled={isFetching}
        onPageChange={setPage}
      />
    </div>
  );
};

export default Timesheets;
